import React from "react";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import DoneIcon from "@mui/icons-material/Done";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next";

/**
 * 공통 버튼 컴포넌트
 * @param {Object} props - 버튼 속성
 * @param {string} props.variant - 버튼 변형 (contained, outlined, text)
 * @param {string} props.color - 버튼 색상 (primary, secondary, error, success 등)
 * @param {Function} props.onClick - 클릭 이벤트 핸들러
 * @param {boolean} props.disabled - 비활성화 여부
 * @param {Object} props.sx - 추가 스타일 속성
 * @param {React.ReactNode} props.children - 버튼 내부 텍스트
 * @returns {JSX.Element} 버튼 컴포넌트
 */
export const CustomButton = ({
  variant = "contained",
  color = "primary",
  onClick,
  disabled = false,
  sx = {},
  children,
  startIcon,
  ...props
}) => {
  return (
    <Button
      variant={variant}
      color={color}
      onClick={onClick}
      disabled={disabled}
      startIcon={startIcon}
      sx={{
        textTransform: "none",
        fontWeight: 500,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

export const AddButton = ({
  onClick,
  disabled,
  sx,
  children,
  ...props
}) => {
  const { t } = useTranslation();
  return (
    <CustomButton
      color="primary"
      onClick={onClick}
      disabled={disabled}
      startIcon={<AddIcon />}
      sx={sx}
      {...props}
    >
      {children || t("pages.aps.plan.buttons.add")}
    </CustomButton>
  );
};

export const DeleteButton = ({
  onClick,
  disabled,
  sx,
  children,
  ...props
}) => {
  const { t } = useTranslation();
  return (
    <CustomButton
      color="error"
      onClick={onClick}
      disabled={disabled}
      startIcon={<DeleteIcon />}
      sx={sx}
      {...props}
    >
      {children || t("pages.aps.plan.buttons.delete")}
    </CustomButton>
  );
};

export const EditButton = ({
  onClick,
  disabled,
  isEditing = false,
  sx,
  children,
  ...props
}) => {
  const { t } = useTranslation();
  return (
    <CustomButton
      color={isEditing ? "success" : "primary"}
      onClick={onClick}
      disabled={disabled}
      startIcon={<ModeEditIcon />}
      sx={sx}
      {...props}
    >
      {children || t("pages.aps.plan.buttons.edit")}
    </CustomButton>
  );
};

export const SelectButton = ({
  onClick,
  disabled,
  sx,
  children,
  ...props
}) => {
  const { t } = useTranslation();
  return (
    <CustomButton
      color="success"
      onClick={onClick}
      disabled={disabled}
      startIcon={<DoneIcon />}
      sx={sx}
      {...props}
    >
      {children || t("pages.aps.plan.buttons.select")}
    </CustomButton>
  );
};

export const SaveButton = ({
  onClick,
  disabled,
  sx,
  children,
  ...props
}) => {
  const { t } = useTranslation();
  return (
    <CustomButton
      color="primary"
      onClick={onClick}
      disabled={disabled}
      startIcon={<SaveIcon />}
      sx={sx}
      {...props}
    >
      {children || t("pages.aps.plan.buttons.save")}
    </CustomButton>
  );
};

export const CancelButton = ({
  onClick,
  disabled,
  sx,
  children,
  ...props
}) => {
  const { t } = useTranslation();
  return (
    <CustomButton
      color="error"
      onClick={onClick}
      disabled={disabled}
      startIcon={<CancelIcon />}
      sx={sx}
      {...props}
    >
      {children || t("pages.aps.plan.buttons.cancel")}
    </CustomButton>
  );
};

export const CloseButton = ({
  onClick,
  disabled,
  sx,
  children,
  ...props
}) => {
  const { t } = useTranslation();
  return (
    <CustomButton
      color="error"
      onClick={onClick}
      disabled={disabled}
      startIcon={<CancelIcon />}
      sx={sx}
      {...props}
    >
      {children || t("pages.aps.plan.buttons.close")}
    </CustomButton>
  );
};

const Buttons = {
  Add: AddButton,
  Delete: DeleteButton,
  Edit: EditButton,
  Select: SelectButton,
  Save: SaveButton,
  Cancel: CancelButton,
  Close: CloseButton,
  Custom: CustomButton,
};

export default Buttons;
